/**
 * Where a sign-up came from, for the campaign that sends people to
 * /kom-i-gang (first /bli-medlem, which redirects there with its query) from
 * Facebook and Instagram ads.
 *
 * On any page a visitor lands on, UTM parameters and Meta's fbclid in the
 * address are kept in localStorage for seven days. localStorage rather than
 * sessionStorage, because signing up with Vipps or Facebook leaves the site
 * and can come back in another tab. When the sign-up succeeds, the kept values
 * go into the `sign_up` dataLayer event, and are then forgotten so a later
 * sign-up on the same device is not credited to the same ad.
 *
 * `signup_source` says how the visitor reached the campaign page:
 *   campaign  - an ad (UTM parameters or fbclid in the address)
 *   footer    - ?fra=footer in the address. The footer's own link stopped adding
 *               it on 14 October (Guro wanted a clean /kom-i-gang); copies of
 *               the old link still count.
 *   direct    - neither
 *
 * Storage can be missing or throw (private windows, blocked site data), so
 * every read and write is guarded: attribution is a nice-to-have and must
 * never break a page or a sign-up.
 */

const STORAGE_KEY = 'zuprize.signupAttribution';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
export const SOURCE_CAMPAIGN = 'campaign';
export const SOURCE_FOOTER = 'footer';
export const SOURCE_DIRECT = 'direct';
export const FOOTER_PARAM = 'fra';
export const FOOTER_VALUE = 'footer';

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

const read = (now = Date.now()) => {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved && now - saved.at < TTL_MS ? saved : null;
  } catch (e) {
    return null;
  }
};

const write = value => {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // Nothing to do: attribution is optional.
  }
};

/**
 * What an address says about where the visitor came from, or null when it
 * says nothing.
 *
 * @param {string} search - location.search
 * @returns {Object|null}
 */
export const attributionFromSearch = search => {
  const params = new URLSearchParams(search || '');
  const utm = UTM_KEYS.reduce((acc, key) => {
    const value = params.get(key);
    return value ? { ...acc, [key]: value.slice(0, 200) } : acc;
  }, {});
  const fbclid = params.get('fbclid');
  if (Object.keys(utm).length > 0 || fbclid) {
    return { source: SOURCE_CAMPAIGN, ...utm, ...(fbclid ? { fbclid: fbclid.slice(0, 500) } : {}) };
  }
  if (params.get(FOOTER_PARAM) === FOOTER_VALUE) {
    return { source: SOURCE_FOOTER };
  }
  return null;
};

/**
 * Keep what this address says, if anything. An ad visit is never overwritten
 * by a later footer click within the seven days: the ad brought them.
 *
 * @param {string} search - location.search
 * @param {number} [now]
 */
export const captureSignupAttribution = (search, now = Date.now()) => {
  const found = attributionFromSearch(search);
  if (!found) {
    return;
  }
  const saved = read(now);
  if (found.source === SOURCE_FOOTER && saved?.source === SOURCE_CAMPAIGN) {
    return;
  }
  write({ ...found, at: now });
};

/**
 * The keys to add to the `sign_up` event.
 *
 * @param {number} [now]
 * @returns {Object} e.g. { signup_source: 'campaign', utm_source: 'facebook', fbclid: '…' }
 */
export const signupAttributionParams = (now = Date.now()) => {
  const saved = read(now);
  if (!saved) {
    return { signup_source: SOURCE_DIRECT };
  }
  const { source, at, ...rest } = saved;
  return { signup_source: source, ...rest };
};

/** Forget the kept values once a sign-up has used them. */
export const clearSignupAttribution = () => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to do.
  }
};
